/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import {
  SpeechInstance,
  SpeechToTextInstance,
  TextToSpeechInstance
} from '../../../../ts/speech';
import {
  AsyncAppThunk,
  CreateAsyncThunk,
  textToSpeechModelElevenLabs,
  textToSpeechVoiceElevenLabs
} from '../../../../ts/state';
import { DEFAULT_CACHE } from '../../../ia/store/config';
import {
  addErrorMessage,
  addMessage,
  ToastStatus
} from '../../../ia/store/messages/actions';
import * as crnUtil from '../../../ia/util/crn';
import { createdObjectEvents } from '../../../ia/util/trackers/segment';
import * as speechApi from '../../api/speech';
import { getPreferredPrefix } from '../../helpers/speech-helpers';
import {
  STTInstanceCreateNotRequired,
  TTSInstanceCreateNotRequired
} from '../phone/actions';
import { Voice as messages } from './messages';
import {
  selectSpeechToTextInstanceById,
  selectTextToSpeechInstanceById
} from './selectors';

export const listSpeechToTextInstances = createAsyncThunk<
  SpeechInstance[],
  void,
  CreateAsyncThunk
>('voice/listSpeechToTextInstances', async (__, { dispatch }) => {
  try {
    // False below means ONLY this accounts instances.
    const response = await speechApi.getSpeechToTextInstances(false);
    return response.instances;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const listSpeechToTextInstancesIfRequired = (): AsyncAppThunk<any> => {
  return (dispatch, getState) => {
    const { listStatus, listFetched } = getState()?.voice?.speechToText;
    // avoid concurrent
    if (listStatus === ApiStatus.LOADING) {
      return Promise.resolve();
    }
    if (listFetched && Date.now() - listFetched < DEFAULT_CACHE) {
      return Promise.resolve();
    }
    return dispatch(listSpeechToTextInstances());
  };
};

export const listTextToSpeechInstances = createAsyncThunk<
  SpeechInstance[],
  void,
  CreateAsyncThunk
>('voice/listTextToSpeechInstances', async (__, { dispatch }) => {
  try {
    // False below means ONLY this accounts instances.
    const response = await speechApi.getTextToSpeechInstances(false);
    return response.instances;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const listTextToSpeechInstancesIfRequired = (): AsyncAppThunk<any> => {
  return (dispatch, getState) => {
    const { listStatus, listFetched } = getState()?.voice?.textToSpeech;
    // avoid concurrent
    if (listStatus === ApiStatus.LOADING) {
      return Promise.resolve();
    }
    if (listFetched && Date.now() - listFetched < DEFAULT_CACHE) {
      return Promise.resolve();
    }
    return dispatch(listTextToSpeechInstances());
  };
};

export const listSpeechToTextInstanceUrl = createAsyncThunk<string, string>(
  'voice/listSpeechToTextInstanceUrl',
  async (instanceId, { dispatch }) => {
    try {
      const url = await speechApi.getSpeechToTextInstanceUrl(instanceId);
      return url;
    } catch (err) {
      dispatch(addErrorMessage(err));
      throw err;
    }
  }
);

export const loadSpeechToTextInstanceInCpd = createAsyncThunk<
  {
    models;
  },
  string
>('voice/loadSpeechToTextInstanceInCpd', async (instanceId, { dispatch }) => {
  try {
    const models = await speechApi.getSpeechToTextInstanceModels(instanceId);
    return models;
  } catch (err) {
    dispatch(addErrorMessage(err));
    throw err;
  }
});

export const loadSpeechToTextInstance = createAsyncThunk<
  SpeechToTextInstance,
  string,
  CreateAsyncThunk
>(
  'voice/loadSpeechToTextInstance',
  async (instanceId, { dispatch, getState }) => {
    try {
      if (!crnUtil.isCrn(instanceId)) {
        throw new Error(messages.badInstanceId.id);
      }

      const shallowInstance = selectSpeechToTextInstanceById(
        getState(),
        instanceId
      );
      const instance =
        shallowInstance?.models?.length > 0
          ? shallowInstance
          : await speechApi.getSpeechToTextInstance(instanceId);

      // if instance doesn't have credentials, get them.
      if (instance.state === 'active' && !instance.credentials) {
        // Create credentials...
        const credentials = await speechApi.createSpeechToTextCredentials(
          instance.id
        );
        instance.credentials = credentials;
      }
      if (instance.state !== 'active') {
        throw new Error(messages.notActive.id);
      }
      // combines loaded instance with existing instance to keep existing instance up to date.
      return _.merge({}, shallowInstance, instance);
    } catch (error) {
      if (error.message === messages.badInstanceId.id) {
        dispatch(
          addErrorMessage({
            status: 'error',
            message: messages.badInstanceId,
            values: {
              instanceId: instanceId
            }
          })
        );
      } else if (error.message === messages.notActive.id) {
        dispatch(
          addErrorMessage({
            status: 'error',
            message: messages.notActive,
            values: {
              instanceId: instanceId
            }
          })
        );
      } else {
        dispatch(addErrorMessage(error));
      }
      throw error;
    }
  }
);
export const createSpeechToTextInstance = createAsyncThunk<
  void,
  void,
  CreateAsyncThunk
>(
  `voice/createSpeechToTextInstance`,
  async (_params, { dispatch, rejectWithValue }) => {
    try {
      const sttInstance = await speechApi.createSpeechToTextInstance(
        getPreferredPrefix()
      );
      const credentials = await speechApi.createSpeechToTextCredentials(
        sttInstance.id
      );
      sttInstance.credentials = credentials;
      dispatch(STTInstanceCreateNotRequired());
      await dispatch(loadSpeechToTextInstance(sttInstance.id));
      dispatch(
        addMessage({
          message: messages.createdSttInstance,
          status: ToastStatus.SUCCESS
        })
      );
      createdObjectEvents.stt({
        object: sttInstance.name,
        resultValue: 'success',
        successFlag: true
      });
    } catch (error) {
      dispatch(addErrorMessage(error));
      if (error?.response?.data) {
        return rejectWithValue(error?.response?.data);
      }
      throw error;
    }
  }
);
export const listTextToSpeechInstanceUrl = createAsyncThunk<string, string>(
  'voice/listTextToSpeechInstanceUrl',
  async (instanceId, { dispatch }) => {
    try {
      const url = await speechApi.getTextToSpeechInstanceUrl(instanceId);
      return url;
    } catch (err) {
      dispatch(addErrorMessage(err));
      throw err;
    }
  }
);

export const loadTextToSpeechInstanceInCpd = createAsyncThunk<
  {
    voices;
  },
  string
>('voice/loadTextToSpeechInstanceInCpd', async (instanceId, { dispatch }) => {
  try {
    const voices = await speechApi.getTextToSpeechInstanceVoices(instanceId);
    return voices;
  } catch (err) {
    dispatch(addErrorMessage(err));
    throw err;
  }
});
export const loadTextToSpeechInstance = createAsyncThunk<
  TextToSpeechInstance,
  string,
  CreateAsyncThunk
>(
  'voice/loadTextToSpeechInstance',
  async (instanceId, { dispatch, getState }) => {
    try {
      if (!crnUtil.isCrn(instanceId)) {
        throw new Error(messages.badInstanceId.id);
      }
      const shallowInstance = selectTextToSpeechInstanceById(
        getState(),
        instanceId
      );
      const instance =
        shallowInstance?.voices?.length > 0
          ? shallowInstance
          : await speechApi.getTextToSpeechInstance(instanceId);
      // if instance doesn't have credentials, get them.
      if (instance.state === 'active' && !instance.credentials) {
        // Create credentials...
        const credentials = await speechApi.createTextToSpeechCredentials(
          instance.id
        );
        instance.credentials = credentials;
      }
      if (instance.state !== 'active') {
        throw new Error(messages.notActive.id);
      }
      return _.merge({}, shallowInstance, instance);
    } catch (error) {
      if (error.message === messages.badInstanceId.id) {
        dispatch(
          addErrorMessage({
            status: 'error',
            message: messages.badInstanceId,
            values: {
              instanceId: instanceId
            }
          })
        );
      } else if (error.message === messages.notActive.id) {
        dispatch(
          addErrorMessage({
            status: 'error',
            message: messages.notActive,
            values: {
              instanceId: instanceId
            }
          })
        );
      } else {
        dispatch(addErrorMessage(error));
      }
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);
export const createTextToSpeechInstance = createAsyncThunk<
  void,
  void,
  CreateAsyncThunk
>(
  `voice/createTextToSpeechInstance`,
  async (_params, { dispatch, rejectWithValue }) => {
    try {
      const ttsinstance = await speechApi.createTextToSpeechInstance(
        getPreferredPrefix()
      );
      const credentials = await speechApi.createTextToSpeechCredentials(
        ttsinstance.id
      );
      ttsinstance.credentials = credentials;
      dispatch(TTSInstanceCreateNotRequired());
      await dispatch(loadTextToSpeechInstance(ttsinstance.id));
      dispatch(
        addMessage({
          message: messages.createdTtsInstance,
          status: ToastStatus.SUCCESS
        })
      );
      createdObjectEvents.tts({
        object: ttsinstance.name,
        resultValue: 'success',
        successFlag: true
      });
    } catch (error) {
      dispatch(addErrorMessage(error));
      if (error?.response?.data) {
        return rejectWithValue(error?.response?.data);
      }
      throw error;
    }
  }
);

export const loadElevenLabsModels = createAsyncThunk<
  textToSpeechModelElevenLabs[],
  string,
  {
    rejectValue: { message: string; code?: number };
  }
>(
  'voice/loadElevenLabsModels',
  async (apiKey, { dispatch, rejectWithValue }) => {
    try {
      const data = await speechApi.fetchElevenLabsVoiceModels(apiKey);
      return data;
    } catch (err) {
      // Show toast notification & send back detailed message for inline error
      dispatch(
        addErrorMessage({
          status: 'error',
          message: err?.response?.data?.detail?.message || 'Unknown error'
        })
      );
      const errorPayload = {
        message: err?.response?.data?.detail?.message || 'Unknown error',
        code: err?.response?.status
      };
      return rejectWithValue(errorPayload);
    }
  }
);

export const loadElevenLabsVoices = createAsyncThunk<
  textToSpeechVoiceElevenLabs[],
  string,
  CreateAsyncThunk
>('voice/loadElevenLabsVoices', async (apiKey, { dispatch }) => {
  try {
    const data = await speechApi.fetchElevenLabsVoices(apiKey);
    return data.voices || [];
  } catch (err) {
    // Send a toast notification
    dispatch(
      addErrorMessage(err?.response?.data?.detail?.message || 'Unknown error')
    );
  }
});

export const resetSpeechToTextInstance = createAction(
  'voice/resetSpeechToText'
);
export const resetTextToSpeechInstance = createAction(
  'voice/resetTextToSpeech'
);
